/**
 * Shared UI primitives used across the admin editors (Rules editor,
 * ServerStatsEditor, etc.). Kept tiny and dependency-free.
 */

import Markdown from "@/components/site/Markdown";
import { useEffect, useRef, useState } from "react";
import { Bold, Italic, Underline, Heading1, Heading2, List, ListOrdered, Link2, Code, Quote, Palette, Highlighter } from "lucide-react";

// Curated palette used by the Color toolbar button. First swatch is "clear"
// which strips any inline color span from the selection.
const TEXT_COLOR_SWATCHES = [
  { value: null, label: "Clear color" },
  { value: "#C0A875", label: "Gold" },
  { value: "#C0392B", label: "Red" },
  { value: "#4ADE80", label: "Green" },
  { value: "#F5D97A", label: "Yellow" },
  { value: "#7EC4CF", label: "Cyan" },
  { value: "#B58FE0", label: "Purple" },
  { value: "#E27B7B", label: "Rose" },
  { value: "#8D9A87", label: "Slate" },
  { value: "#E2E4DF", label: "White" },
];
// Highlight (background) palette — tuned for a dark UI: muted tints stay legible.
const HIGHLIGHT_SWATCHES = [
  { value: null, label: "Clear highlight" },
  { value: "#F5D97A", label: "Marker yellow" },
  { value: "#4ADE80", label: "Neon green" },
  { value: "#7EC4CF", label: "Sticky cyan" },
  { value: "#E27B7B", label: "Alert rose" },
  { value: "#C0A875", label: "Kraft gold" },
  { value: "#B58FE0", label: "Purple" },
  { value: "#8B1A1A", label: "Blood red" },
  { value: "#1E3A5F", label: "Deep blue" },
  { value: "#0D110C", label: "Blackout" },
];
const RECENT_COLORS_KEY = "wlou.md.recentColors";
const RECENT_COLORS_MAX = 8;

// A hex like "#a1b2c3" (6-digit only — the color input always yields 6-digit).
const HEX_RE = /^#[0-9a-fA-F]{6}$/;

// Rough luminance check — used by the highlighter to pick a legible text
// color (near-black on light bg, near-white on dark bg).
function _isLightHex(hex) {
  if (!HEX_RE.test(hex)) return false;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  // Perceptual luminance (BT.601)
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6;
}

export function TextField({ label, value, onChange, testid, hint }) {
  return (
    <div>
      <label className="block font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87] mb-1.5">
        {label}
      </label>
      <input
        value={value}
        data-testid={testid}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-xs focus:border-[#C0A875] focus:outline-none"
      />
      {hint && <p className="mt-1 font-mono-ui text-[9px] uppercase tracking-[0.2em] text-[#5C6B51]">{hint}</p>}
    </div>
  );
}

export function TextArea({ label, value, onChange, rows = 4, testid }) {
  return (
    <div>
      <label className="block font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87] mb-1.5">
        {label}
      </label>
      <textarea
        value={value}
        rows={rows}
        data-testid={testid}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-xs focus:border-[#C0A875] focus:outline-none resize-y"
      />
    </div>
  );
}

/**
 * Markdown-aware textarea with a formatting toolbar AND live rendered
 * preview panel side-by-side. Toolbar buttons wrap/prefix the current
 * selection with the right markdown snippet — no manual typing.
 *
 * Underlying storage stays plain markdown so the public renderer never
 * has to change.
 */
export function MarkdownTextArea({ label, value, onChange, rows = 4, testid }) {
  const ref = useRef(null);
  const [colorOpen, setColorOpen] = useState(false);
  const [highlightOpen, setHighlightOpen] = useState(false);
  const [customHex, setCustomHex] = useState("#C0A875");
  const [recentColors, setRecentColors] = useState(() => {
    try {
      const raw = localStorage.getItem(RECENT_COLORS_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.filter((c) => HEX_RE.test(c)) : [];
    } catch {
      return [];
    }
  });

  // Persist recent colors so admins get back their palette across reloads.
  useEffect(() => {
    try {
      localStorage.setItem(RECENT_COLORS_KEY, JSON.stringify(recentColors));
    } catch {
      /* quota / private mode — ignore */
    }
  }, [recentColors]);

  const rememberColor = (hex) => {
    if (!hex || !HEX_RE.test(hex)) return;
    setRecentColors((prev) => {
      const next = [hex, ...prev.filter((c) => c.toLowerCase() !== hex.toLowerCase())];
      return next.slice(0, RECENT_COLORS_MAX);
    });
  };

  // Wrap current selection with a prefix/suffix (bold, italic, code, etc.).
  // If nothing is selected, drop the pair around the caret and put focus
  // inside so the user can type into the empty pair.
  const wrap = (prefix, suffix = prefix, placeholder = "") => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = value.substring(start, end) || placeholder;
    const next = value.substring(0, start) + prefix + selected + suffix + value.substring(end);
    onChange(next);
    // Restore selection inside the pair on the next tick.
    setTimeout(() => {
      el.focus();
      const s = start + prefix.length;
      const e = s + selected.length;
      el.setSelectionRange(s, e);
    }, 0);
  };

  // Apply an inline color to the current selection by wrapping it in an
  // HTML span. Works because our Markdown renderer passes raw HTML through
  // rehype-raw. Passing `color = null` strips any existing color-span from
  // the selection instead.
  const applyColor = (color) => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    let selected = value.substring(start, end);
    if (!selected) selected = "text";

    // Strip an outermost color span if the selection already is one.
    const outerColorSpan = /^<span style="color:\s*[^"]+">([\s\S]*)<\/span>$/;
    const trimmed = selected.trim().match(outerColorSpan);

    let replacement;
    if (color === null) {
      // Clear color — unwrap if wrapped, otherwise leave the text alone.
      replacement = trimmed ? trimmed[1] : selected;
    } else if (trimmed) {
      // Re-color an existing span in place.
      replacement = `<span style="color: ${color}">${trimmed[1]}</span>`;
    } else {
      replacement = `<span style="color: ${color}">${selected}</span>`;
    }

    const next = value.substring(0, start) + replacement + value.substring(end);
    onChange(next);
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(start, start + replacement.length);
    }, 0);
    if (color) rememberColor(color);
    setColorOpen(false);
  };

  // Same wrap idea as applyColor but for a background highlight. We add
  // a small horizontal padding and rounded corners so a "marker" swathe
  // is legible even when it lands on a single word. `mark` tag is the
  // semantic HTML choice but we style inline so ReactMarkdown / rehype-raw
  // doesn't need any extra config.
  const applyHighlight = (color) => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    let selected = value.substring(start, end);
    if (!selected) selected = "highlighted";

    // Strip an outer background-color span if the selection already is one.
    const outerBgSpan = /^<span style="background:\s*[^;]+;\s*padding:[^;]+;\s*border-radius:[^"]+">([\s\S]*)<\/span>$/;
    const trimmed = selected.trim().match(outerBgSpan);

    let replacement;
    if (color === null) {
      replacement = trimmed ? trimmed[1] : selected;
    } else {
      const inner = trimmed ? trimmed[1] : selected;
      // Auto-flip text color to black on very light backgrounds so
      // yellow/green highlights stay legible on dark backgrounds AND
      // still look right when the underlying text is coloured.
      const textColor = _isLightHex(color) ? "#0D110C" : "#F5E9C8";
      replacement =
        `<span style="background: ${color}; color: ${textColor}; padding: 0 4px; border-radius: 2px">${inner}</span>`;
    }

    const next = value.substring(0, start) + replacement + value.substring(end);
    onChange(next);
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(start, start + replacement.length);
    }, 0);
    if (color) rememberColor(color);
    setHighlightOpen(false);
  };

  // Prefix each line of the current selection with a marker ("# ", "- ",
  // "1. ", ">") — used for headings, bullet lists, numbered lists, quotes.
  const prefixLines = (marker, numbered = false) => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    // Expand to full lines around the current selection.
    const before = value.substring(0, start);
    const lineStart = before.lastIndexOf("\n") + 1;
    const after = value.substring(end);
    const idxNewline = after.indexOf("\n");
    const lineEnd = end + (idxNewline === -1 ? after.length : idxNewline);
    const block = value.substring(lineStart, lineEnd) || "text";
    const lines = block.split("\n");
    const prefixed = lines.map((l, i) => (numbered ? `${i + 1}. ${l}` : `${marker}${l}`)).join("\n");
    const next = value.substring(0, lineStart) + prefixed + value.substring(lineEnd);
    onChange(next);
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(lineStart, lineStart + prefixed.length);
    }, 0);
  };

  const insertLink = () => {
    const el = ref.current;
    if (!el) return;
    const url = window.prompt("Link URL:");
    if (!url) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = value.substring(start, end) || "link text";
    const md = `[${selected}](${url})`;
    const next = value.substring(0, start) + md + value.substring(end);
    onChange(next);
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(start + 1, start + 1 + selected.length);
    }, 0);
  };

  const btn = (Icon, title, onClick, testId) => (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()} // keep textarea focus
      onClick={onClick}
      title={title}
      aria-label={title}
      data-testid={testId}
      className="w-8 h-8 border border-[#283524] text-[#8D9A87] hover:border-[#C0A875] hover:text-[#C0A875] flex items-center justify-center transition-colors"
    >
      <Icon size={13} />
    </button>
  );

  const previewId = testid ? `${testid}-preview` : undefined;

  return (
    <div>
      <div className="flex items-baseline justify-between mb-1.5">
        <label className="block font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87]">
          {label}
        </label>
        <span className="font-mono-ui text-[9px] uppercase tracking-[0.2em] text-[#5C6B51]">
          // Toolbar → live preview
        </span>
      </div>

      {/* Toolbar */}
      <div
        className="bg-[#0D110C] border border-[#283524] border-b-0 px-2 py-1.5 flex flex-wrap items-center gap-1"
        data-testid={testid ? `${testid}-toolbar` : undefined}
      >
        {btn(Bold, "Bold  (Ctrl+B)", () => wrap("**", "**", "bold text"), testid && `${testid}-bold`)}
        {btn(Italic, "Italic  (Ctrl+I)", () => wrap("*", "*", "italic text"), testid && `${testid}-italic`)}
        {btn(Underline, "Underline  (Ctrl+U)", () => wrap("<u>", "</u>", "underlined text"), testid && `${testid}-underline`)}
        <span className="w-px h-5 bg-[#283524] mx-1" />
        {btn(Heading1, "Heading 1", () => prefixLines("# "), testid && `${testid}-h1`)}
        {btn(Heading2, "Heading 2", () => prefixLines("## "), testid && `${testid}-h2`)}
        <span className="w-px h-5 bg-[#283524] mx-1" />
        {btn(List, "Bullet list", () => prefixLines("- "), testid && `${testid}-ul`)}
        {btn(ListOrdered, "Numbered list", () => prefixLines("", true), testid && `${testid}-ol`)}
        <span className="w-px h-5 bg-[#283524] mx-1" />
        {btn(Link2, "Insert link", insertLink, testid && `${testid}-link`)}
        {btn(Code, "Inline code", () => wrap("`", "`", "code"), testid && `${testid}-code`)}
        {btn(Quote, "Quote", () => prefixLines("> "), testid && `${testid}-quote`)}
        <span className="w-px h-5 bg-[#283524] mx-1" />
        {/* Color picker — wraps selection in <span style="color:X">…</span> */}
        <div className="relative">
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setColorOpen((v) => !v)}
            title="Text color"
            aria-label="Text color"
            aria-haspopup="true"
            aria-expanded={colorOpen}
            data-testid={testid ? `${testid}-color` : undefined}
            className="w-8 h-8 border border-[#283524] text-[#8D9A87] hover:border-[#C0A875] hover:text-[#C0A875] flex items-center justify-center transition-colors"
          >
            <Palette size={13} />
          </button>
          {colorOpen && (
            <div
              className="absolute top-full right-0 mt-1 z-30 bg-[#0D110C] border border-[#C0A875]/50 shadow-2xl p-3 w-[260px] space-y-3"
              data-testid={testid ? `${testid}-color-popover` : undefined}
            >
              <div>
                <div className="font-mono-ui text-[9px] uppercase tracking-[0.25em] text-[#5C6B51] mb-1.5">
                  // Palette
                </div>
                <div className="grid grid-cols-5 gap-1">
                  {TEXT_COLOR_SWATCHES.map((sw) => (
                    <button
                      key={sw.value || "clear"}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => applyColor(sw.value)}
                      title={sw.label}
                      data-testid={
                        testid ? `${testid}-color-${sw.value ? sw.value.replace("#", "") : "clear"}` : undefined
                      }
                      className={`w-8 h-8 border-2 flex items-center justify-center ${
                        sw.value ? "border-[#283524] hover:border-[#E2E4DF]" : "border-[#5C6B51] hover:border-[#C0392B]"
                      }`}
                      style={sw.value ? { background: sw.value } : { background: "transparent" }}
                    >
                      {!sw.value && <span className="text-[#C0392B] text-[10px] font-bold">✕</span>}
                    </button>
                  ))}
                </div>
              </div>

              {recentColors.length > 0 && (
                <div>
                  <div className="font-mono-ui text-[9px] uppercase tracking-[0.25em] text-[#5C6B51] mb-1.5">
                    // Recent
                  </div>
                  <div className="grid grid-cols-8 gap-1">
                    {recentColors.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => applyColor(c)}
                        title={c}
                        data-testid={testid ? `${testid}-color-recent-${c.replace("#", "")}` : undefined}
                        className="w-5 h-5 border border-[#283524] hover:border-[#E2E4DF]"
                        style={{ background: c }}
                      />
                    ))}
                  </div>
                </div>
              )}

              <div>
                <div className="font-mono-ui text-[9px] uppercase tracking-[0.25em] text-[#5C6B51] mb-1.5">
                  // Custom (any color)
                </div>
                <div className="flex items-center gap-2">
                  <label
                    className="relative w-8 h-8 border-2 border-[#283524] hover:border-[#C0A875] cursor-pointer overflow-hidden shrink-0"
                    style={{ background: HEX_RE.test(customHex) ? customHex : "#000" }}
                    title="Pick any color"
                  >
                    <input
                      type="color"
                      value={HEX_RE.test(customHex) ? customHex : "#C0A875"}
                      onChange={(e) => setCustomHex(e.target.value)}
                      onMouseDown={(e) => e.stopPropagation()}
                      data-testid={testid ? `${testid}-color-native-input` : undefined}
                      className="absolute inset-0 opacity-0 cursor-pointer"
                    />
                  </label>
                  <input
                    type="text"
                    value={customHex}
                    onChange={(e) => {
                      let v = e.target.value.trim();
                      if (v && !v.startsWith("#")) v = "#" + v;
                      setCustomHex(v.slice(0, 7));
                    }}
                    placeholder="#RRGGBB"
                    maxLength={7}
                    data-testid={testid ? `${testid}-color-hex-input` : undefined}
                    className={`flex-1 bg-[#0D110C] border px-2 py-1.5 font-mono-ui text-xs uppercase focus:outline-none ${
                      HEX_RE.test(customHex) ? "border-[#283524] text-[#E2E4DF] focus:border-[#C0A875]" : "border-[#C0392B]/60 text-[#C0392B]"
                    }`}
                  />
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => HEX_RE.test(customHex) && applyColor(customHex)}
                    disabled={!HEX_RE.test(customHex)}
                    data-testid={testid ? `${testid}-color-hex-apply` : undefined}
                    className="bg-[#C0A875] hover:bg-[#a99362] disabled:bg-[#283524] disabled:text-[#8D9A87] text-[#0D110C] px-3 py-1.5 font-mono-ui text-[10px] uppercase tracking-[0.2em] font-semibold"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Highlighter — wraps selection in a <span> with a background color */}
        <div className="relative">
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setHighlightOpen((v) => !v)}
            title="Highlight (background color)"
            aria-label="Highlight"
            aria-haspopup="true"
            aria-expanded={highlightOpen}
            data-testid={testid ? `${testid}-highlight` : undefined}
            className="w-8 h-8 border border-[#283524] text-[#8D9A87] hover:border-[#F5D97A] hover:text-[#F5D97A] flex items-center justify-center transition-colors"
          >
            <Highlighter size={13} />
          </button>
          {highlightOpen && (
            <div
              className="absolute top-full right-0 mt-1 z-30 bg-[#0D110C] border border-[#F5D97A]/50 shadow-2xl p-3 w-[220px] space-y-2"
              data-testid={testid ? `${testid}-highlight-popover` : undefined}
            >
              <div className="font-mono-ui text-[9px] uppercase tracking-[0.25em] text-[#5C6B51]">
                // Highlight
              </div>
              <div className="grid grid-cols-5 gap-1">
                {HIGHLIGHT_SWATCHES.map((sw) => (
                  <button
                    key={sw.value || "clear"}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => applyHighlight(sw.value)}
                    title={sw.label}
                    data-testid={
                      testid ? `${testid}-highlight-${sw.value ? sw.value.replace("#", "") : "clear"}` : undefined
                    }
                    className={`w-8 h-8 border-2 flex items-center justify-center ${
                      sw.value ? "border-[#283524] hover:border-[#F5D97A]" : "border-[#5C6B51] hover:border-[#C0392B]"
                    }`}
                    style={sw.value ? { background: sw.value } : { background: "transparent" }}
                  >
                    {!sw.value && <span className="text-[#C0392B] text-[10px] font-bold">✕</span>}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 pt-1 border-t border-[#283524]">
                <span className="font-mono-ui text-[9px] uppercase tracking-[0.25em] text-[#5C6B51]">
                  Custom:
                </span>
                <label
                  className="relative w-6 h-6 border-2 border-[#283524] hover:border-[#F5D97A] cursor-pointer overflow-hidden shrink-0"
                  style={{ background: HEX_RE.test(customHex) ? customHex : "#000" }}
                  title="Pick any highlight color"
                >
                  <input
                    type="color"
                    value={HEX_RE.test(customHex) ? customHex : "#F5D97A"}
                    onChange={(e) => setCustomHex(e.target.value)}
                    onMouseDown={(e) => e.stopPropagation()}
                    data-testid={testid ? `${testid}-highlight-native-input` : undefined}
                    className="absolute inset-0 opacity-0 cursor-pointer"
                  />
                </label>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => HEX_RE.test(customHex) && applyHighlight(customHex)}
                  disabled={!HEX_RE.test(customHex)}
                  data-testid={testid ? `${testid}-highlight-hex-apply` : undefined}
                  className="ml-auto bg-[#F5D97A] hover:bg-[#e0c760] disabled:bg-[#283524] disabled:text-[#8D9A87] text-[#0D110C] px-2 py-1 font-mono-ui text-[10px] uppercase tracking-[0.2em] font-semibold"
                >
                  Apply
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Textarea + preview split */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
        <textarea
          ref={ref}
          value={value}
          rows={rows}
          data-testid={testid}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            // Keyboard shortcuts — Ctrl/Cmd + B/I/U
            if (!(e.metaKey || e.ctrlKey)) return;
            const key = e.key.toLowerCase();
            if (key === "b") { e.preventDefault(); wrap("**", "**", "bold text"); }
            else if (key === "i") { e.preventDefault(); wrap("*", "*", "italic text"); }
            else if (key === "u") { e.preventDefault(); wrap("<u>", "</u>", "underlined text"); }
          }}
          className="w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-xs focus:border-[#C0A875] focus:outline-none resize-y"
          placeholder="Type here — use the toolbar above (or Ctrl+B / Ctrl+I / Ctrl+U)"
        />
        <div
          className="min-h-[6rem] bg-[#0D110C] border border-dashed border-[#283524] text-[#A8B5A0] text-xs px-3 py-2 overflow-auto"
          data-testid={previewId}
        >
          {value ? (
            <Markdown>{value}</Markdown>
          ) : (
            <span className="font-mono-ui text-[10px] uppercase tracking-[0.2em] text-[#5C6B51]">
              // Preview will render here…
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Inline color-picker row. Renders palette swatches plus an "any color" tile
 * that opens the browser's native color picker for full-spectrum selection,
 * and a small hex text input for typing an exact value.
 *
 * Props:
 *   value          — current hex (or null/undefined = "no override / default")
 *   onChange(hex)  — called when the user picks / types a new color
 *   onClear        — optional; if provided renders a "clear" tile that calls it
 *   clearLabel     — label for the clear tile (defaults to "A")
 *   clearTitle     — tooltip for the clear tile
 *   palette        — array of hex color strings for quick-pick swatches
 *   size           — "sm" (compact, inline) or "md" (larger, for section highlight)
 *   testidPrefix   — prefix for generated data-testid attributes
 */
export function HexColorRow({
  value,
  onChange,
  onClear,
  clearLabel = "A",
  clearTitle = "Clear",
  palette = [],
  size = "md",
  testidPrefix,
}) {
  const isSm = size === "sm";
  const cls = isSm ? "w-5 h-5" : "w-8 h-8";
  const [draft, setDraft] = useState(HEX_RE.test(value || "") ? value : "");

  // Keep hex text field in sync when parent value changes (e.g. clicking a swatch).
  useEffect(() => {
    setDraft(HEX_RE.test(value || "") ? value : "");
  }, [value]);

  const currentLc = (value || "").toLowerCase();
  const inPalette = palette.some((c) => c.toLowerCase() === currentLc);
  const isCustomSelected = !!value && HEX_RE.test(value) && !inPalette;

  const commitDraft = () => {
    if (HEX_RE.test(draft)) onChange(draft.toUpperCase());
  };

  return (
    <div className="inline-flex flex-wrap items-center gap-1.5">
      {onClear && (
        <button
          type="button"
          onClick={onClear}
          title={clearTitle}
          data-testid={testidPrefix ? `${testidPrefix}-clear` : undefined}
          className={`${cls} border-2 flex items-center justify-center font-mono-ui ${
            isSm ? "text-[9px]" : "text-[10px]"
          } ${!value ? "border-[#E2E4DF] text-[#E2E4DF]" : "border-[#283524] text-[#5C6B51] hover:border-[#8D9A87]"}`}
        >
          {clearLabel}
        </button>
      )}
      {palette.map((c) => {
        const selected = c.toLowerCase() === currentLc;
        return (
          <button
            key={c}
            type="button"
            onClick={() => onChange(c)}
            title={c}
            aria-label={c}
            data-testid={testidPrefix ? `${testidPrefix}-${c.replace("#", "")}` : undefined}
            className={`${cls} border-2 transition-transform hover:scale-110 ${
              selected ? "border-[#E2E4DF]" : "border-[#283524]"
            }`}
            style={{ background: c }}
          />
        );
      })}

      {/* Show the currently-picked custom color (non-palette) as a highlighted swatch. */}
      {isCustomSelected && (
        <span
          title={`Current: ${value}`}
          data-testid={testidPrefix ? `${testidPrefix}-current-custom` : undefined}
          className={`${cls} border-2 border-[#E2E4DF]`}
          style={{ background: value }}
        />
      )}

      {/* Native color picker — full spectrum via hidden <input type="color">. */}
      <label
        className={`${cls} border-2 border-[#283524] hover:border-[#C0A875] relative overflow-hidden cursor-pointer`}
        title="Pick any color (full spectrum)"
      >
        <input
          type="color"
          value={HEX_RE.test(value || "") ? value : "#C0A875"}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          data-testid={testidPrefix ? `${testidPrefix}-native` : undefined}
          className="absolute inset-0 opacity-0 cursor-pointer"
          aria-label="Pick any color"
        />
        <span
          aria-hidden="true"
          className="absolute inset-0 flex items-center justify-center text-white font-bold pointer-events-none"
          style={{
            background:
              "conic-gradient(from 0deg, #ff2d2d, #ffcc00, #6ee06e, #00c4d1, #6a8fff, #b46fe0, #ff2d2d)",
            fontSize: isSm ? "10px" : "13px",
            textShadow: "0 0 2px rgba(0,0,0,0.7)",
          }}
        >
          +
        </span>
      </label>

      {/* Hex text input — for typing an exact color. Hidden in the very compact
          subsection rows (size=sm) to keep the toolbar tidy; still available in
          the roomier section highlight card. */}
      {!isSm && (
        <input
          type="text"
          value={draft}
          onChange={(e) => {
            let v = e.target.value.trim();
            if (v && !v.startsWith("#")) v = "#" + v;
            setDraft(v.slice(0, 7).toUpperCase());
          }}
          onBlur={commitDraft}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commitDraft(); }
          }}
          placeholder="#RRGGBB"
          maxLength={7}
          data-testid={testidPrefix ? `${testidPrefix}-hex-input` : undefined}
          className={`ml-1 w-[86px] bg-[#0D110C] border px-2 py-1 font-mono-ui text-[10px] uppercase focus:outline-none ${
            !draft || HEX_RE.test(draft)
              ? "border-[#283524] text-[#E2E4DF] focus:border-[#C0A875]"
              : "border-[#C0392B]/60 text-[#C0392B]"
          }`}
        />
      )}
    </div>
  );
}

export function IconPicker({ label, value, icons, onChange }) {
  return (
    <div>
      <label className="block font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87] mb-1.5">
        {label}
      </label>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-xs focus:border-[#C0A875] focus:outline-none"
      >
        {icons.map((name) => (
          <option key={name} value={name}>{name}</option>
        ))}
      </select>
    </div>
  );
}

export function IconBtn({ children, onClick, disabled, title, danger, testid }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`w-7 h-7 border flex items-center justify-center transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
        danger
          ? "border-[#C0392B] text-[#C0392B] hover:bg-[#C0392B]/15"
          : "border-[#283524] text-[#8D9A87] hover:border-[#C0A875] hover:text-[#C0A875]"
      }`}
    >
      {children}
    </button>
  );
}

/**
 * Compact cheat-sheet strip shown at the top of the Rules + Guide editors so
 * super-admins know which markdown snippets are available in the text fields
 * (welcome body, subsection items, guide paragraph/callout blocks, table
 * cells, etc.). Kept as a single row so it doesn't shove content down.
 */
export function FormattingHint() {
  const pairs = [
    ["**bold**", "bold"],
    ["*italic*", "italic"],
    ["<u>underline</u>", "underline"],
    ["# H1", "H1"],
    ["## H2", "H2"],
    ["- item", "bullet"],
    ["1. step", "numbered"],
    ["[text](url)", "link"],
    ["`code`", "code"],
  ];
  return (
    <div
      className="bg-[#0D110C] border border-[#283524] px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1"
      data-testid="formatting-hint"
    >
      <span className="font-mono-ui text-[9px] uppercase tracking-[0.3em] text-[#5C6B51] shrink-0">
        // Formatting
      </span>
      {pairs.map(([syntax, label]) => (
        <span key={label} className="inline-flex items-baseline gap-1.5">
          <code className="font-mono-ui text-[10px] text-[#C0A875]">{syntax}</code>
          <span className="font-mono-ui text-[9px] uppercase tracking-[0.15em] text-[#5C6B51]">→ {label}</span>
        </span>
      ))}
    </div>
  );
}
